import { useEffect, useRef, useState } from 'react'
import { CheckCircleFilled, DownOutlined, LoadingOutlined, RightOutlined } from '@ant-design/icons'
import { Tooltip, Typography } from 'antd'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import type { AgentCurationSummary, AgentPreviewRef, AgentStep } from '../lib/agentSteps'
import { formatAnswerWaitDetail } from '../lib/agentSteps'

export type AgentDocRef = {
  item_id: number
  title?: string
  scope?: KnowledgeScope
  created_by?: string | null
  created_at?: string | null
}

export type AgentPreviewTarget = { itemId: number; scope: KnowledgeScope }

type ChatAgentStepsProps = {
  steps: AgentStep[]
  intro?: string
  streaming?: boolean
  docRefs?: AgentDocRef[]
  defaultScope?: KnowledgeScope
  onPreview?: (target: AgentPreviewTarget) => void
}

function stepTitle(step: AgentStep, live: boolean): string {
  if (step.status === 'running' && live && step.kind === 'reasoning' && step.label.endsWith('…')) {
    return '推理中…'
  }
  return step.label
}

function RefsStepBody({
  previewRefs,
  titles,
  defaultScope,
  onPreview,
}: {
  previewRefs?: AgentPreviewRef[]
  titles?: string[]
  defaultScope: KnowledgeScope
  onPreview?: (target: AgentPreviewTarget) => void
}) {
  const refs = previewRefs ?? []
  if (refs.length > 0) {
    return (
      <ul className="chat-agent-step-refs-list">
        {refs.map((r) => {
          const title = r.title?.trim() || `文档 #${r.item_id}`
          return (
            <li key={r.item_id}>
              {onPreview ? (
                <Tooltip title={title} placement="topLeft">
                  <Typography.Link
                    className="chat-agent-step-ref-link"
                    onClick={() => onPreview({ itemId: r.item_id, scope: defaultScope })}
                  >
                    {title}
                  </Typography.Link>
                </Tooltip>
              ) : (
                <Tooltip title={title} placement="topLeft">
                  <span className="chat-agent-step-ref-link">{title}</span>
                </Tooltip>
              )}
            </li>
          )
        })}
      </ul>
    )
  }
  if (titles?.length) {
    return (
      <>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          回答生成后将显示可点击的参考来源；当前检索到：
        </Typography.Text>
        <ul className="chat-agent-step-titles">
          {titles.map((t) => (
            <li key={t}>
              <Tooltip title={t} placement="topLeft">
                <span className="chat-agent-step-title-text">{t}</span>
              </Tooltip>
            </li>
          ))}
        </ul>
      </>
    )
  }
  return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      暂无参考文档
    </Typography.Text>
  )
}

function CurationStepBody({
  curation,
  running,
}: {
  curation: AgentCurationSummary
  running: boolean
}) {
  const skipLabels: Record<string, string> = {
    single_doc: '仅命中单篇文档，无需整理',
    no_model: '未配置对话模型，跳过文档整理',
    parse_failed: '整理结果解析失败，已使用全部检索命中文档',
  }

  if (curation.skipped) {
    const msg = skipLabels[curation.skipReason || ''] || '本次未执行文档整理'
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {msg}
      </Typography.Text>
    )
  }

  return (
    <>
      {curation.overviewNote ? (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          {curation.overviewNote}
        </Typography.Paragraph>
      ) : null}
      {running && (curation.candidates?.length ?? 0) > 0 ? (
        <>
          <Typography.Text strong style={{ fontSize: 12 }}>
            候选文档（{curation.candidates!.length} 篇）
          </Typography.Text>
          <ul className="chat-agent-step-titles">
            {curation.candidates!.map((d) => (
              <li key={`${d.item_id}-${d.title}`}>{d.title}</li>
            ))}
          </ul>
        </>
      ) : null}
      {!running && (curation.related?.length ?? 0) > 0 ? (
        <>
          <Typography.Text strong style={{ fontSize: 12 }}>
            保留相关（{curation.related!.length} 篇）
          </Typography.Text>
          <ul className="chat-agent-step-titles">
            {curation.related!.map((d) => (
              <li key={`${d.item_id}-${d.title}`}>
                {d.title}
                {d.relevance_note ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {' '}
                    — {d.relevance_note}
                  </Typography.Text>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {!running && (curation.excluded?.length ?? 0) > 0 ? (
        <>
          <Typography.Text strong style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
            排除偏题（{curation.excluded!.length} 篇）
          </Typography.Text>
          <ul className="chat-agent-step-titles">
            {curation.excluded!.map((d) => (
              <li key={`ex-${d.item_id}-${d.title}`}>
                {d.title}
                {d.reason ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {' '}
                    — {d.reason}
                  </Typography.Text>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  )
}

function AgentStepCard({
  step,
  live,
  defaultScope,
  onPreview,
}: {
  step: AgentStep
  live: boolean
  defaultScope: KnowledgeScope
  onPreview?: (target: AgentPreviewTarget) => void
}) {
  const running = step.status === 'running' && live
  const [open, setOpen] = useState(false)
  const [waitDetail, setWaitDetail] = useState<string | undefined>(step.detail)
  const isAnswerStream = step.id === 'answer-stream'
  const isStreamDetail =
    step.kind === 'reasoning' && !!(waitDetail?.trim() || step.detail?.trim())
  const streamDetailRef = useRef<HTMLPreElement>(null)
  const hasRefPreview = (step.previewRefs?.length ?? 0) > 0
  const isRefs = step.kind === 'refs' || (step.kind === 'retrieval' && hasRefPreview)
  const isCuration = step.id === 'hit-curation' && !!step.curation
  const hasCurationBody =
    isCuration &&
    (running ||
      step.curation!.skipped ||
      (step.curation!.related?.length ?? 0) > 0 ||
      (step.curation!.candidates?.length ?? 0) > 0 ||
      (step.curation!.excluded?.length ?? 0) > 0)
  const hasDetail = isRefs
    ? (step.previewRefs?.length ?? 0) > 0 || !!(step.titles && step.titles.length > 0)
    : hasCurationBody ||
      !!(waitDetail?.trim() || step.detail?.trim() || (step.titles && step.titles.length > 0))
  const canExpand = hasDetail

  useEffect(() => {
    if (step.id === 'hit-curation' && (running || hasCurationBody)) {
      setOpen(true)
      return
    }
    if (running && (step.kind === 'reasoning' || step.kind === 'judge')) {
      setOpen(true)
    }
  }, [running, step.kind, step.detail, step.id, hasCurationBody])

  useEffect(() => {
    if (step.id === 'answer-prep' && step.status === 'done') {
      setOpen(false)
    }
    if (step.id === 'answer-write' && step.status === 'done') {
      setOpen(false)
    }
  }, [step.id, step.status])

  useEffect(() => {
    setWaitDetail(step.detail)
  }, [step.detail])

  useEffect(() => {
    if (!running || !isAnswerStream || !step.startedAt) return
    const tick = () => setWaitDetail(formatAnswerWaitDetail(step.startedAt))
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [running, isAnswerStream, step.startedAt])

  useEffect(() => {
    if (!isStreamDetail || !streamDetailRef.current) return
    const el = streamDetailRef.current
    el.scrollTop = el.scrollHeight
  }, [isStreamDetail, waitDetail, step.detail, running])

  return (
    <div
      className={`chat-agent-step chat-agent-step--${step.kind} chat-agent-step--${step.status}${
        running ? ' chat-agent-step--live' : ''
      }`}
    >
      <button
        type="button"
        className="chat-agent-step-head"
        onClick={() => (canExpand ? setOpen((v) => !v) : undefined)}
        disabled={!canExpand}
      >
        <span className="chat-agent-step-icon">
          {running ? (
            <LoadingOutlined spin />
          ) : step.status === 'done' ? (
            <CheckCircleFilled />
          ) : null}
        </span>
        <span className="chat-agent-step-label">{stepTitle(step, live)}</span>
        {step.kind === 'refs' ? (
          <RightOutlined className="chat-agent-step-chevron" />
        ) : canExpand ? (
          <DownOutlined className={`chat-agent-step-chevron${open ? ' chat-agent-step-chevron-open' : ''}`} />
        ) : null}
      </button>
      {open && canExpand ? (
        <div className="chat-agent-step-body">
          {isRefs ? (
            <RefsStepBody
              previewRefs={step.previewRefs}
              titles={step.titles}
              defaultScope={defaultScope}
              onPreview={onPreview}
            />
          ) : isCuration && step.curation ? (
            <CurationStepBody curation={step.curation} running={running} />
          ) : (
            <>
              {waitDetail?.trim() || step.detail?.trim() ? (
                <pre
                  ref={isStreamDetail ? streamDetailRef : undefined}
                  className={`chat-agent-step-detail${isStreamDetail ? ' chat-agent-step-detail--stream' : ''}`}
                >
                  {waitDetail?.trim() ? waitDetail : step.detail}
                </pre>
              ) : null}
              {step.titles && step.titles.length > 0 ? (
                <ul className="chat-agent-step-titles">
                  {step.titles.map((t) => (
                    <li key={t}>
                      <Tooltip title={t} placement="topLeft">
                        <span className="chat-agent-step-title-text">{t}</span>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

export function ChatAgentSteps({
  steps,
  intro,
  streaming = false,
  defaultScope = 'enterprise',
  onPreview,
}: ChatAgentStepsProps) {
  if (!steps.length) return null

  return (
    <div className="chat-agent-steps">
      {intro ? <div className="chat-agent-steps-intro">{intro}</div> : null}
      <div className="chat-agent-steps-list">
        {steps.map((step) => (
          <AgentStepCard
            key={step.id}
            step={step}
            live={streaming}
            defaultScope={defaultScope}
            onPreview={onPreview}
          />
        ))}
      </div>
    </div>
  )
}
